import OutlinedButtonWithArrow from "@/ui/common/bit/OutlinedButtonWithArrow"
import { menuNavigationClick } from "@/utils/gtm"
import styled from "@emotion/styled"
import {
  Box,
  ButtonBase,
  Drawer,
  IconButton,
  ListItem,
  Typography,
} from "@mui/material"
import Image from "next/image"
import { Dispatch, SetStateAction, useState } from "react"
import { getUmbracoMediaUrl } from "utils"
import { transformProperties } from "utils/simplifyData"
import { iconsMap } from "../../../public/icons/iconsMap"
import { colors, mediaQueries, sizes } from "../../theme/theme"
import MegaMenuItem from "./MegaMenuItem"

interface MobileMenuProps {
  data: any
  setSelectedInnerMenu: Dispatch<SetStateAction<any>>
  selectedInnerMenu: any
  handleClose: () => void
}

const StyledMenuItems = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledListItem = styled(ListItem)`
  padding-inline-start: 0px;
  padding-inline-end: 0px;
  padding-top: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${colors.lightGray};
  justify-content: space-between;
  &:last-child {
    border-bottom: none;
  }
`

const StyledImageContainer = styled(Box)`
  width: 100%;
  height: 252px;
  position: relative;
  margin-top: ${sizes.baseQuadric};
  border-radius: ${sizes.baseDouble};
  overflow: hidden;
`

const StyledInnerTitle = styled(Typography)`
  ${mediaQueries.tabletDown} {
    font-size: 22px;
  }
  margin-top: ${sizes.baseQuadric};
  margin-bottom: ${sizes.baseDouble};
`

const StyledDrawer = styled(Drawer)`
  .MuiDrawer-paper {
    padding: ${sizes.baseDouble};
    box-shadow: -2px 0px 8px rgba(0, 0, 0, 0.1);
    z-index: 101;
    width: 100%;
  }
`

const StyledBackButton = styled(ButtonBase)`
  display: flex;
  align-items: center;
  padding: ${sizes.baseSingle};
`
const StyledDrawerHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${sizes.baseQuadric};
`
const StyledDrawerTitle = styled(Typography)`
  ${mediaQueries.tabletDown} {
    font-size: 25px;
    font-weight: 700;
  }
`
const MobileMenu = ({
  data,
  setSelectedInnerMenu,
  selectedInnerMenu,
  handleClose,
}: MobileMenuProps) => {
  const RedArrow = iconsMap["left_red_arrow"]
  const [selectedDisplayMenu, setSelectedDisplayMenu] = useState<any[]>([])
  const [selectedItemMenu, setSelectedItemMenu] = useState<string>("")
  const HamburgerClose: React.FC = iconsMap["HamburgerClose"]
  const RightBlackArrow: React.FC = iconsMap["right_black_arrow"]
  const moreSites = transformProperties(data?.properties)?.moreSites
    ?.blocks?.[0]
  const dropdownList = transformProperties(moreSites?.properties)?.dropdownList
    ?.links

  const toggleInnerMenu = (item: any) => {
    if (!item.properties) {
      setSelectedDisplayMenu([])
      return
    }

    setSelectedDisplayMenu([
      ...item.properties.find(
        (prop: any) => prop.propertyName === "menuRightArea",
      ).propertyValue.blocks,
      ...item.properties.find(
        (prop: any) => prop.propertyName === "menuLeftArea",
      ).propertyValue.blocks,
    ])
    const { image, title } = transformProperties(item.properties)
    setSelectedInnerMenu(image.mediaItems[0]?.url)
    setSelectedItemMenu(title?.value || "")
  }

  const handleCloseDrawer = () => {
    setSelectedDisplayMenu([])
  }
  const transformedData = transformProperties(data.properties)

  const sendMenuEvent = (itemName: string, categoryName?: string) => {
    menuNavigationClick({
      menuType: "תפריט מובייל",
      menuCategory1: itemName,
      menuCategory2: categoryName,
    })
  }

  return (
    <StyledMenuItems>
      {transformedData &&
        transformedData?.megaMenuItemsList.blocks.map(
          (item: any, index: number) => {
            const { title } = transformProperties(item.properties)
            return (
              <StyledListItem key={`mobile-menu-item-${index}-${title?.value}`}>
                <ButtonBase
                  onClick={() => toggleInnerMenu(item)}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Typography variant="body2">{title?.value}</Typography>
                  <RedArrow />
                </ButtonBase>
              </StyledListItem>
            )
          },
        )}
      <StyledDrawer
        anchor={"right"}
        open={selectedDisplayMenu?.length > 0}
        onClose={handleCloseDrawer}
        ModalProps={{
          keepMounted: false,
        }}
      >
        <Box>
          <StyledDrawerHeader>
            <StyledBackButton onClick={handleCloseDrawer}>
              <RightBlackArrow />
            </StyledBackButton>
            <StyledDrawerTitle>{selectedItemMenu}</StyledDrawerTitle>
            <IconButton onClick={handleClose}>
              <HamburgerClose />
            </IconButton>
          </StyledDrawerHeader>

          {(Array.isArray(selectedDisplayMenu) ? selectedDisplayMenu : []).map(
            (child: any, index: number) => {
              const { title, links } = transformProperties(child?.properties)
              return (
                <Box key={`mobile-menu-drawer-${index}-${title?.value}`}>
                  {title?.value && (
                    <StyledInnerTitle fontWeight={700} color={colors.red}>
                      {title.value}
                    </StyledInnerTitle>
                  )}

                  {links.links.map((linkItem: any, linkIndex: number) => (
                    <MegaMenuItem
                      key={linkIndex}
                      item={linkItem}
                      outerIndex={linkIndex}
                      outerChildren={undefined}
                      handleItemClick={(_href, buttonText) => {
                        handleCloseDrawer()
                        setTimeout(() => {
                          // so the user will see the animation of the drawer closing
                          handleClose()
                        }, 200)
                        sendMenuEvent(buttonText || "", selectedItemMenu)
                      }}
                    />
                  ))}
                </Box>
              )
            },
          )}

          {!!selectedInnerMenu && selectedInnerMenu.length > 0 && (
            <StyledImageContainer>
              <Image
                alt="selected Inner Menu Image"
                fill
                src={getUmbracoMediaUrl(selectedInnerMenu || "")}
              />
            </StyledImageContainer>
          )}
        </Box>
      </StyledDrawer>
      {dropdownList && dropdownList.length > 0 && (
        <StyledListItem>
          {dropdownList.map((item: any, index: number) => {
            const { name, url, type } = item
            return (
              <OutlinedButtonWithArrow
                key={`mobile-menu-more-sites-${index}-${name}`}
                onClick={() => {
                  sendMenuEvent(name || "")
                  window.open(url, type === "EXTERNAL" ? "_blank" : "_self")
                }}
              >
                <Typography variant="body2">{name}</Typography>
              </OutlinedButtonWithArrow>
            )
          })}
        </StyledListItem>
      )}
    </StyledMenuItems>
  )
}

export default MobileMenu
